import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { deepEqual, iaRa } from 'lib/helpers/utility';
import { useFilters } from 'lib/hooks/queries/filters/useFilters';

import { applyFilterHandler } from 'components/shared/Filter/lib/helpers/handleAppliedFilter';

export const useReactFilters = ({ name, defaultFilters }) => {
  const [filters, setFilters] = useState(null);
  const [initialFilters, setInitialFilters] = useState(null);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [isEditingFilter, setIsEditingFilter] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const view = searchParams.get('view');

  const { data: filtersData, isFetched } = useFilters({
    params: { category: name },
    dependencies: [name],
    options: { enabled: Boolean(name?.length) }
  });

  const appliedFilters = useCallback(() => {
    if (!isFetched) return;

    let appliedFilters, appliedInitialFilters;

    if (view && filtersData?.filters) {
      const selectedFilter = iaRa(filtersData.filters).find((f) => f.name === view);
      appliedFilters = applyFilterHandler(defaultFilters, selectedFilter || null);
      appliedInitialFilters = applyFilterHandler(defaultFilters, selectedFilter || null);
      if (selectedFilter?.default) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('page');
          newParams.delete('sort');
          newParams.delete('order');
          return newParams;
        });
      }
    } else if (filtersData?.default?.filters && !location?.state?.disableDefaultFilter) {
      appliedFilters = applyFilterHandler(defaultFilters, filtersData.default);
      appliedInitialFilters = applyFilterHandler(defaultFilters, filtersData.default);

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('page');
        newParams.delete('sort');
        newParams.delete('order');
        newParams.set('view', filtersData.default.name);
        return newParams;
      });
    } else {
      appliedFilters = applyFilterHandler(defaultFilters, null);
      appliedInitialFilters = applyFilterHandler(defaultFilters, null);
    }

    setFilters(appliedFilters);
    setInitialFilters(appliedInitialFilters);
    setFiltersInitialized(true);
    return appliedFilters;
  }, [isFetched, filtersInitialized, name]);

  useEffect(() => {
    appliedFilters();
    window.addEventListener('nav-link-clicked', appliedFilters);
    return () => {
      window.removeEventListener('nav-link-clicked', appliedFilters);
    };
  }, [appliedFilters]);

  useEffect(() => {
    if (!filtersInitialized || !filtersData?.filters) return;

    if (view) {
      const selectedFilter = iaRa(filtersData.filters).find((f) => f.name === view);
      const applied = applyFilterHandler(defaultFilters, selectedFilter || null);
      const isSameFilters = deepEqual(filters, applied);
      if (!isSameFilters) {
        setFilters(applied);
        setInitialFilters(applied);
      }
    }
  }, [view, filtersData, filtersInitialized]);

  const isInitialFilter = useMemo(() => {
    return deepEqual(initialFilters, filters);
  }, [initialFilters, filters]);

  const resetFilters = useCallback(() => {
    const appliedFilters = applyFilterHandler(defaultFilters, null);
    setFilters(appliedFilters);
    setInitialFilters(appliedFilters);
  }, [defaultFilters]);

  const updateFilters = useCallback(
    (newFilter) => {
      const updatedFilters = applyFilterHandler(defaultFilters, newFilter);
      setFilters(updatedFilters);
      setInitialFilters(updatedFilters);
    },
    [setFilters, setInitialFilters]
  );

  const changeFilters = useCallback(
    async (updater, isSwitchItemView = false) => {
      setFilters((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const isInitial = deepEqual(initialFilters, next);
        const isEditing = !!view && !isInitial && !isSwitchItemView;
        setIsEditingFilter(isEditing);
        return next;
      });
    },
    [initialFilters, setIsEditingFilter, view]
  );

  return {
    filters,
    initialFilters,
    filtersInitialized,
    isEditingFilter,
    isInitialFilter,
    resetFilters,
    updateFilters,
    changeFilters
  };
};
